import { supabase } from "@/integrations/supabase/client";
import { getGeoIpData } from "@/utils/geoIpService";
import type { TrackShareMeta } from "@/hooks/useTrackShareMeta";

export type ShareMethod = "native" | "copy" | "web_target";

function detectDevice(): "mobile" | "tablet" | "desktop" | "unknown" {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent || "";
  if (/iPad|Tablet/i.test(ua)) return "tablet";
  if (/Mobi|Android|iPhone/i.test(ua)) return "mobile";
  return "desktop";
}

interface LogArgs {
  trackId: string;
  meta: TrackShareMeta | null;
  method: ShareMethod;
  target?: string | null;
  artistId?: string | null;
}

/**
 * Fire-and-forget share event logger. Never throws, never blocks UX.
 * Only logs when meta indicates sponsor or artist attribution.
 */
export async function logSponsoredShare({
  trackId,
  meta,
  method,
  target,
  artistId,
}: LogArgs): Promise<void> {
  try {
    if (!meta || meta.source === "none") return;

    const [{ data: auth }, geo] = await Promise.all([
      supabase.auth.getUser(),
      getGeoIpData().catch(() => null),
    ]);

    const payload = {
      track_id: trackId,
      sponsor_id: meta.source === "sponsor" ? meta.attributionId : null,
      artist_id: meta.source === "artist" ? meta.attributionId : artistId ?? null,
      attribution_source: meta.source,
      hashtags: meta.hashtags ?? [],
      share_method: method,
      share_target: target ?? null,
      country_code: geo?.country_code ?? null,
      city: geo?.city ?? null,
      ip: geo?.ip ?? null,
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      referrer: typeof document !== "undefined" ? document.referrer || null : null,
      device_type: detectDevice(),
      sharer_user_id: auth?.user?.id ?? null,
    };

    await (supabase as any)
      .from("sponsored_share_events")
      .insert(payload);
  } catch (err) {
    console.warn("[sponsoredShareTracking] failed:", err);
  }
}
